import React from "react";
import PropTypes from "prop-types";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "../buildStyles";

/**
 * CircularSpinner is a component that displays a spinning circular loader.
 * It can be customized with the `size`, `color`, and `thickness` props.
 *
 * @param {Object} props - The props for the CircularSpinner component.
 * @param {string} [props.size="25px"] - The width and height of the spinner in pixels.
 * @param {string} [props.color] - The color of the spinner. Defaults to theme.colors.primary.
 * @param {string} [props.thickness="2px"] - The thickness of the spinner's border.
 * @param {Object} [props.style={}] - Additional inline styles to apply to the spinner.
 *
 * @returns {JSX.Element} The rendered CircularSpinner component.
 */
const CircularSpinner = ({
  size = "25px",
  color = "primary",
  thickness = "2px",
  style = {},
  ...props
}) => {
  const {theme} = useTheme();
  const spinnerColor = theme.colors[color] || theme.colors.primary;

  const keyframesName = buildStyles({
    "@keyframes spinnerAnimation": {
      "0%": {transform: "rotate(0deg)"},
      "100%": {transform: "rotate(360deg)"},
    },
  })["@keyframes spinnerAnimation"];

  const styles = buildStyles({
    spinner: {
      width: size,
      height: size,
      borderStyle: "solid",
      borderRadius: "50%",
      borderWidth: thickness,
      borderColor: `${spinnerColor} transparent transparent transparent`,
      animation: `${keyframesName} 0.7s linear infinite`,
    },
  });

  return (
    <div
      style={{
        width: size,
        height: size,
        ...style,
      }}
      className={styles.spinner}
      {...props}
    />
  );
};

CircularSpinner.propTypes = {
  size: PropTypes.string,
  color: PropTypes.string,
  thickness: PropTypes.string,
  style: PropTypes.object,
};

export default CircularSpinner;
